/** Helpers texte purs, partagés backend/frontend. */

// `\p{M}` = catégorie Unicode « Mark » : exactement les diacritiques combinants que
// la décomposition NFD isole. Préféré à une plage de code points, illisible en source.
const COMBINING_MARKS = /\p{M}/gu;

/** Minuscule, sans accents. Base de toutes les recherches (sites, annuaire, inventaire). */
export function normTxt(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(COMBINING_MARKS, '');
}

/**
 * Téléphone au format national français, groupé par deux : `+33776359742` → `07 76 35 97 42`.
 * Pour l'**affichage** seulement ; le lien `tel:` garde la valeur brute (indicatif compris).
 * Un numéro non reconnu est rendu tel quel plutôt que déformé.
 */
export function formatPhoneFr(raw?: string | null): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const national = digits.startsWith('33') ? `0${digits.slice(2)}` : digits;
  if (national.length !== 10) return raw.trim();
  return national.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
}

const DATE_FR = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
const DATE_TIME_FR = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Date au format court français : `10 juil. 2026`. Entrée : chaîne ISO. */
export function formatDateFr(iso: string): string {
  return DATE_FR.format(new Date(iso));
}

/** Date et heure au format français : `10 juil. 2026, 14:32`. Entrée : chaîne ISO. */
export function formatDateTimeFr(iso: string): string {
  return DATE_TIME_FR.format(new Date(iso));
}

/**
 * Filtre + trie une liste par pertinence : les préfixes d'abord, puis l'ordre naturel.
 * Reproduit le comportement de la combobox du prototype (insensible aux accents et à la casse).
 *
 * @param haystack renvoie le texte cherchable d'un élément (ex. `${code} ${name}`)
 * @param compare  départage les ex aequo (défaut : ordre alphabétique français)
 */
export function searchByPrefix<T>(
  items: readonly T[],
  query: string,
  haystack: (item: T) => string,
  compare?: (a: T, b: T) => number,
): T[] {
  const q = normTxt(query.trim());
  if (!q) return [...items];

  return items
    .filter((item) => normTxt(haystack(item)).includes(q))
    .sort((a, b) => {
      const aPrefix = normTxt(haystack(a)).startsWith(q) ? 0 : 1;
      const bPrefix = normTxt(haystack(b)).startsWith(q) ? 0 : 1;
      if (aPrefix !== bPrefix) return aPrefix - bPrefix;
      return compare ? compare(a, b) : haystack(a).localeCompare(haystack(b), 'fr');
    });
}
